import type { StudentActionType } from './studentActions';
import type { FePageContext } from './types';

type RoutePattern = {
  pattern: RegExp;
  route: string;
  asn?: number;
  cls?: number;
  studentAction?: StudentActionType;
};

const ROUTE_PATTERNS: RoutePattern[] = [
  {
    pattern: /^\/student\/assignments\/([^/]+)\/practice$/,
    route: '/student/assignments/:code/practice',
    asn: 1,
    studentAction: 'ASSIGNMENT_PRACTICE_START',
  },
  {
    pattern: /^\/student\/assignments\/([^/]+)\/submission$/,
    route: '/student/assignments/:code/submission',
    asn: 1,
    studentAction: 'OFFLINE_WORK_VIEW',
  },
  {
    pattern: /^\/student\/assignments\/([^/]+)$/,
    route: '/student/assignments/:code',
    asn: 1,
    studentAction: 'ASSIGNMENT_VIEW',
  },
  {
    pattern: /^\/student\/classrooms\/([^/]+)$/,
    route: '/student/classrooms/:code',
    cls: 1,
    studentAction: 'CLASSROOM_VIEW',
  },
  { pattern: /^\/assignments\/([^/]+)$/, route: '/assignments/:code', asn: 1 },
  { pattern: /^\/classrooms\/([^/]+)$/, route: '/classrooms/:code', cls: 1 },
];

export type StudentRouteAction = {
  action: StudentActionType;
  asn?: string;
  cls?: string;
};

export function resolveStudentRouteAction(pathname: string): StudentRouteAction | null {
  for (const item of ROUTE_PATTERNS) {
    if (!item.studentAction) {
      continue;
    }
    const match = pathname.match(item.pattern);
    if (!match) {
      continue;
    }
    return {
      action: item.studentAction,
      asn: item.asn != null ? match[item.asn] : undefined,
      cls: item.cls != null ? match[item.cls] : undefined,
    };
  }
  return null;
}

export function normalizeApiPath(path: string): string {
  if (!path) return path;
  const [pathname, query] = path.split('?');
  const normalized = pathname
    .replace(/\/api\/student\/assignments\/[^/]+/g, '/api/student/assignments/:code')
    .replace(/\/api\/teacher\/assignments\/[^/]+/g, '/api/teacher/assignments/:code')
    .replace(/\/api\/student\/classrooms\/[^/]+/g, '/api/student/classrooms/:code')
    .replace(/\/api\/teacher\/classrooms\/[^/]+/g, '/api/teacher/classrooms/:code')
    .replace(/\/api\/classrooms\/[^/]+/g, '/api/classrooms/:code')
    .replace(/\/offline-assets\/[^/]+/g, '/offline-assets/:code')
    .replace(/\/sessions\/\d+/g, '/sessions/:id');
  return query ? `${normalized}?${query}` : normalized;
}

export function resolvePageContext(pathname: string): FePageContext {
  for (const item of ROUTE_PATTERNS) {
    const match = pathname.match(item.pattern);
    if (!match) continue;
    const ctx: FePageContext = { path: pathname, route: item.route };
    if (item.asn != null) ctx.asn = match[item.asn];
    if (item.cls != null) ctx.cls = match[item.cls];
    return ctx;
  }
  return { path: pathname, route: pathname };
}

export function shouldTrackApiPath(path: string): boolean {
  if (!path.startsWith('/api/')) return false;
  if (path.startsWith('/api/client-telemetry')) return false;
  if (path.startsWith('/api/activity/heartbeat')) return false;
  return true;
}
